import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../utils/api';
import { ROUTINES_API, ROSTER_API } from '../utils/api';
import { 
  normalizeRoles, 
  normalizeDancerList,
  getRoutineType,
  getRoutineTypeLabel,
  getRoutinePrice,
  extractDancersFromRoutines,
  calculateTotalPrice
} from '../utils/competitionUtils';
import React from 'react';
import { UseCompetitionDataReturn } from '../types/competition';
import { Routine, RegistrationData } from '../types/registration';

type CompetitionDataProps = {
  registrationId?: string;
  addRoutine?: (routineId: string, options: Partial<Routine>) => Promise<void>;
  updateRoutine?: (routineCloneId: string, updateData: Partial<Routine>) => Promise<void>;
  removeRoutine?: (routineCloneId: string) => Promise<void>;
  loadRegistrationRoutines?: () => Promise<void>;
  routines?: Routine[];
  [key: string]: any;
};

/**
 * Custom hook to manage the competition data, routines, and state
 * Updated to use local state first approach - changes are only sent to backend when user clicks continue
 */
const useCompetitionData = (
  registrationData: CompetitionDataProps,
  updateRegistrationData: (data: any, isComplete?: boolean) => Promise<any>,
  competitionPricingRules: any[],
  ageDivisions: any[]
): UseCompetitionDataReturn => {
  console.log('useCompetitionData hook called with registrationData:', registrationData);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  
  // Initialize selected routines from registrationData.routines - LOCAL STATE FIRST
  const [selectedRoutines, setSelectedRoutines] = useState<Routine[]>(() => {
    console.log('Initializing selectedRoutines state with registrationData:', registrationData);
    
    // Use routines array as the source of truth (new structure)
    const initialRoutines = registrationData.routines || [];
    
    console.log('Initial selectedRoutines state:', initialRoutines);
    return initialRoutines;
  });
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Modal state management
  const [editingRoutine, setEditingRoutine] = useState<Routine | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  
  // Type change confirmation modal state
  const [showTypeChangeModal, setShowTypeChangeModal] = useState<boolean>(false);
  const [pendingRoutineChange, setPendingRoutineChange] = useState<any>(null);
  const [typeChangeInfo, setTypeChangeInfo] = useState<{ oldType: string, newType: string }>({ oldType: '', newType: '' });

  // Add new state for tracking routine being added
  const [routineBeingAdded, setRoutineBeingAdded] = useState<string | null>(null);

  // Internal price calculation functions
  const getPriceForRoutine = useCallback((routine: Routine): number => {
    return getRoutinePrice(routine, competitionPricingRules);
  }, [competitionPricingRules]);

  const getTotalPrice = useCallback((): number => {
    return calculateTotalPrice(selectedRoutines, competitionPricingRules);
  }, [selectedRoutines, competitionPricingRules]);

  // Fetch routines and roster data
  useEffect(() => {
    const fetchData = async () => {
      console.log('useCompetitionData useEffect triggered. Fetching routines/roster...');
      try {
        setLoading(true);
        
        // Fetch both routines and roster in parallel
        const [routinesData, rosterData] = await Promise.all([
          fetchWithAuth(ROUTINES_API),
          fetchWithAuth(ROSTER_API)
        ]);
        
        const routinesList = routinesData.routines || [];
        const rosterList = rosterData.roster || [];
        
        // Filter roster to only include dancers
        const dancersOnly = rosterList.filter((member: any) => {
          const roles = normalizeRoles(member.role);
          return roles.includes("Dancer");
        });
        
        setRoster(dancersOnly);
        
        // Process routines to ensure consistent property names
        const processedRoutines = routinesList
          .filter((routine: any) => 
            // Filter routines that are not templates and not registration-specific
            !routine.isTemplate && !routine.registrationSpecific
          )
          .map((routine: any) => {
            // Normalize dancers array
            const dancersList = normalizeDancerList(routine.dancers);
            
            // Determine routine type based on dancer count
            const dancerCount = dancersList.length;
            const routineType = getRoutineType(dancerCount, competitionPricingRules);
            
            // Ensure all required properties exist
            return {
              ...routine,
              routineId: routine.routineId || routine.id || `routine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              title: routine.title || routine.name || 'Untitled Routine',
              name: routine.name || routine.title || 'Untitled Routine',
              type: routineType,
              typeLabel: getRoutineTypeLabel(routineType),
              dancerCount: dancerCount,
              style: routine.style || '',
              dancers: dancersList
            };
          });
        
        setRoutines(processedRoutines);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [competitionPricingRules]);

  // Toggle routine selection - LOCAL STATE FIRST APPROACH
  const toggleRoutineSelection = useCallback((routine: Routine) => {
    // Check if this routine is already selected
    const isSelected = selectedRoutines.some(r => 
      r.originalRoutineId === routine.routineId || 
      r.routineId === routine.routineId ||
      r.routineCloneId === routine.routineCloneId
    );
    
    if (isSelected) {
      // Remove routine from local state only
      console.log('Removing routine from local selection:', routine.routineId);
      
      setSelectedRoutines(prev => {
        const updatedRoutines = prev.filter(r => {
          // Get all possible IDs for the routine being removed
          const routineToRemoveIds = [
            routine.routineId,
            routine.originalRoutineId,
            routine.routineCloneId
          ].filter(Boolean); // Remove null/undefined values
          
          // Get all possible IDs for the current routine in the list
          const currentRoutineIds = [
            r.routineId,
            r.originalRoutineId,
            r.routineCloneId
          ].filter(Boolean);
          
          // Keep the routine if none of its IDs match any of the IDs of the routine to remove
          return !routineToRemoveIds.some(id => currentRoutineIds.includes(id));
        });
        
        // Update registration data with the new state
        updateRegistrationData({
          routines: updatedRoutines
        });
        
        return updatedRoutines;
      });
      
    } else {
      // Add routine to local state only
      console.log('Adding routine to local selection:', routine.routineId);
      
      // Set loading state for UI feedback
      setRoutineBeingAdded(routine.routineId || null);
      
      // Prepare the routine with default options
      const routineToAdd = {
        ...routine,
        originalRoutineId: routine.routineId, // Store the original ID
        routineCloneId: `clone-${routine.routineId}-${Date.now()}`, // Generate a temporary clone ID
        pendingCreation: true // Flag to indicate this routine exists only in local state
      };
      
      // Update local state immediately
      setSelectedRoutines(prev => {
        const updatedRoutines = [...prev, routineToAdd];
        
        // Update registration data with the new state
        updateRegistrationData({
          routines: updatedRoutines
        });
        
        return updatedRoutines;
      });
      
      // Clear loading state
      setRoutineBeingAdded(null);
    }
  }, [selectedRoutines, updateRegistrationData]);

  // Update selected routine options - LOCAL STATE FIRST APPROACH
  const updateSelectedRoutineOptions = useCallback((routineId: string, options: any) => {
    console.log(`Updating routine ${routineId} with options:`, options);
    
    // Update local state immediately
    setSelectedRoutines(prev => {
      const updatedRoutines = prev.map(r => {
        if (r.routineId === routineId || r.routineCloneId === routineId || r.originalRoutineId === routineId) {
          return { ...r, ...options };
        }
        return r;
      });
      
      // Update registration data with the new state
      updateRegistrationData({
        routines: updatedRoutines
      });
      
      return updatedRoutines;
    });
  }, [updateRegistrationData]);

  // Handle edit routine
  const handleEditRoutine = useCallback((routine: Routine) => {
    setEditingRoutine(routine);
    setIsEditModalOpen(true);
  }, []);

  // Handle update routine from edit modal
  const handleUpdateRoutine = useCallback((originalRoutine: Routine, updatedData: any) => {
    const routineId = originalRoutine.routineCloneId || originalRoutine.routineId || '';
    
    // Check if dancer count changed and if it affects the routine type
    const originalDancerCount = Array.isArray(originalRoutine.dancers) ? originalRoutine.dancers.length : 0;
    const newDancerCount = Array.isArray(updatedData.dancers) ? updatedData.dancers.length : 0;
    
    if (originalDancerCount !== newDancerCount) {
      // Determine new type based on dancer count
      const newType = getRoutineType(newDancerCount, competitionPricingRules);
      const oldType = originalRoutine.typeLabel || originalRoutine.type || '';
      
      if (newType !== originalRoutine.type) {
        // Show confirmation modal
        setTypeChangeInfo({
          oldType: oldType || 'Unknown',
          newType: getRoutineTypeLabel(newType) || newType
        });
        setPendingRoutineChange({
          routineId,
          originalRoutine,
          updatedData: {
            ...updatedData,
            type: newType,
            typeLabel: getRoutineTypeLabel(newType)
          }
        });
        setShowTypeChangeModal(true);
        return;
      }
    }
    
    // If no type change or confirmation not needed, update directly
    updateSelectedRoutineOptions(routineId, updatedData);
  }, [competitionPricingRules, updateSelectedRoutineOptions]);

  // Handle routine update completion
  const handleRoutineUpdateComplete = useCallback(() => {
    setEditingRoutine(null);
    setIsEditModalOpen(false);
  }, []);

  // Handle type change confirmation
  const handleConfirmTypeChange = useCallback(() => {
    if (pendingRoutineChange) {
      updateSelectedRoutineOptions(
        pendingRoutineChange.routineId,
        pendingRoutineChange.updatedData
      );
    }
    setShowTypeChangeModal(false);
    setPendingRoutineChange(null);
    setTypeChangeInfo({ oldType: '', newType: '' });
  }, [pendingRoutineChange, updateSelectedRoutineOptions]);

  // Handle type change cancellation
  const handleCancelTypeChange = useCallback(() => {
    setShowTypeChangeModal(false);
    setPendingRoutineChange(null);
    setTypeChangeInfo({ oldType: '', newType: '' });
  }, []);

  // Prepare data for the next step - THIS IS WHERE WE SEND TO BACKEND
  const prepareNextStepData = useCallback(async () => {
    console.log('Preparing competition data for next step and sending to backend...');
    
    try {
      // Process all routines and send to backend
      await processAllRoutines();
      
      // Extract selected routine IDs
      const selectedRoutineIds = selectedRoutines.map(routine => 
        routine.routineCloneId || routine.routineId || ''
      );
      
      // Extract routine options
      const routineOptions = selectedRoutines.reduce((acc, routine) => {
        const routineId = routine.routineCloneId || routine.routineId;
        
        if (routineId) {
          acc[routineId] = {
            style: routine.style || '',
            category: routine.category || '',
            ageGroup: routine.ageGroup || ''
          };
        }
        
        return acc;
      }, {} as Record<string, any>);
      
      // Prepare the competition data object
      const competitionData = {
        selectedRoutineIds,
        routineOptions
      };
      
      // Send final data to backend with completion flag
      await updateRegistrationData(competitionData, true);
      
      return competitionData;
    } catch (error) {
      console.error('Error preparing competition data:', error);
      throw error;
    }
  }, [selectedRoutines, updateRegistrationData]);

  // Helper function to process all routines and send to backend
  const processAllRoutines = async () => {
    if (!registrationData.addRoutine || !registrationData.updateRoutine || !registrationData.removeRoutine) {
      console.log('No routine management functions available, skipping backend processing');
      return;
    }
    
    try {
      console.log('Processing all routines for backend...');
      
      // Track all routines that exist in the backend (have routineCloneId without pendingCreation flag)
      const existingRoutineIds = new Set();
      
      // First pass: create new routines and track existing ones
      for (let i = 0; i < selectedRoutines.length; i++) {
        const routine = selectedRoutines[i];
        
        // If routine already exists in the backend, add to tracking set
        if (routine.routineCloneId && !routine.pendingCreation) {
          existingRoutineIds.add(routine.routineCloneId);
        }
        
        // Skip routines that are already in the backend
        if (!routine.pendingCreation) continue;
        
        console.log(`Creating new routine in backend: ${routine.title || routine.name}`);
        
        try {
          // Call API to create the routine
          await registrationData.addRoutine(routine.originalRoutineId || routine.routineId || '', routine);
          
          console.log(`Successfully created routine: ${routine.title || routine.name}`);
          
          // Mark as no longer pending creation
          selectedRoutines[i] = {
            ...routine,
            pendingCreation: false
          };
          
          // Add to tracking set
          if (routine.routineCloneId) {
            existingRoutineIds.add(routine.routineCloneId);
          }
          
        } catch (error) {
          console.error(`Error creating routine ${routine.title || routine.name}:`, error);
          // Continue processing other routines
        }
      }
      
      // Second pass: update existing routines that have changes
      for (const routine of selectedRoutines) {
        // Skip routines that don't have routineCloneId or are pending creation
        if (!routine.routineCloneId || routine.pendingCreation) continue;
        
        console.log(`Updating routine in backend: ${routine.title || routine.name}`);
        
        try {
          // Call API to update the routine
          await registrationData.updateRoutine(routine.routineCloneId, {
            style: routine.style,
            category: routine.category,
            ageGroup: routine.ageGroup,
            dancers: routine.dancers
          });
          
          console.log(`Successfully updated routine: ${routine.title || routine.name}`);
        } catch (error) {
          console.error(`Error updating routine ${routine.title || routine.name}:`, error);
          // Continue processing other routines
        }
      }

      // Third pass: Check for routines that were previously in the backend but are now deleted from local state
      const previousRoutines = registrationData.routines || [];
      
      // Find routines with routineCloneIds that are no longer in the selected routines list
      for (const prevRoutine of previousRoutines) {
        // Skip if no routineCloneId (wasn't in backend)
        if (!prevRoutine.routineCloneId) continue;
        
        // Skip if it's still in our current selection
        if (existingRoutineIds.has(prevRoutine.routineCloneId)) continue;
        
        console.log(`Routine was removed in UI: ${prevRoutine.title || prevRoutine.name}, deleting from backend...`);
        
        try {
          // This routine was in the backend but is no longer in selectedRoutines - delete it
          await registrationData.removeRoutine(prevRoutine.routineCloneId);
          console.log(`Successfully deleted routine: ${prevRoutine.title || prevRoutine.name}`);
        } catch (error) {
          console.error(`Error deleting routine ${prevRoutine.title || prevRoutine.name}:`, error);
        }
      }
      
    } catch (error) {
      console.error('Error processing routines:', error);
      throw error;
    }
  };

  return {
    loading,
    error,
    routines,
    selectedRoutines,
    searchQuery,
    setSearchQuery,
    toggleRoutineSelection,
    updateSelectedRoutineOptions,
    getTotalPrice,
    getPriceForRoutine,
    prepareNextStepData,
    editingRoutine,
    isEditModalOpen,
    handleEditRoutine,
    handleUpdateRoutine,
    handleRoutineUpdateComplete,
    setIsEditModalOpen,
    setEditingRoutine,
    showTypeChangeModal,
    pendingRoutineChange,
    typeChangeInfo,
    setShowTypeChangeModal,
    setPendingRoutineChange,
    setTypeChangeInfo,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    routineBeingAdded
  };
};

export default useCompetitionData; 